import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useCurrentTalentProfile } from "@/shared";
import useThemeStore from "@/shared/stores/useThemeStore";

import { CARD_AUDIO_SKIP_SECONDS } from "@/cards/common/component/background-players/singleCardAudio";

import {
  getShakaAudioLiveMedia,
  isSameShakaAudioSrc,
  playCardShakaAudio,
  useCardAudioShakaMiniPlayerStore,
} from "@/cards/common/component/background-players/cardAudioShakaMiniPlayerStore";
import { playShakaAudioExclusive } from "@/shared/components/shakaplayer/ShakaPlayerAudio";

import { fetchLyricsContent } from "../../utils/fetchLyricsContent";
import { normalizeLyricsText } from "../../utils/normalizeLyricsText";

type AudioExperienceData = {
  talent?: {
    id?: number;
    avatar?: string;
    name?: string;
    username?: string;
  };

  audio: {
    audioUrl: string;
    preview_url: string;
    cover_image_url?: string;
    image_url?: string;

    description?: string;

    title?: string;

    artistName?: string;
    artist_name?: string;

    lyrics?: string;
    lyricsText?: string;
    lyrics_text?: string;

    lyricsUrl?: string | File;
    lyrics_url?: string | File;

    songTitle?: string;
    song_title?: string;
  };
};

type Props = {
  data: AudioExperienceData;
  isPurchased?: boolean;
  previewDurationSeconds?: number;
};

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export const TaasSingleCardAudioDisplay: React.FC<Props> = ({
  data,
  isPurchased = true,
  previewDurationSeconds = 10,
}) => {
  const { darkMode } = useThemeStore();
  const { currentProfile } = useCurrentTalentProfile();
  const isOwner = currentProfile?.id === data.talent?.id;
  const activeLyricRef = useRef<HTMLElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [lyrics, setLyrics] = useState("");
  const [isLyricsLoading, setIsLyricsLoading] = useState(false);

  /**
   * ---------------------------------------------------------
   * AUDIO DATA
   * ---------------------------------------------------------
   */

  const audioUrl = isOwner
    ? data.audio?.audioUrl
    : isPurchased
      ? data.audio?.audioUrl
      : data.audio?.preview_url;

  const title = data.audio?.songTitle || data.audio?.song_title || data.audio?.title || "Audio";

  const artistName =
    data.audio?.artistName ||
    data.audio?.artist_name ||
    data.talent?.username ||
    data.talent?.name ||
    "";

  const description = data.audio?.description;

  // Prefer image_url; fall through empty strings to cover_image_url / avatar.
  const artworkUrl =
    data?.audio?.image_url || data?.audio?.cover_image_url || data.talent?.avatar || undefined;

  const lyricsUrl = data.audio?.lyricsUrl || data.audio?.lyrics_url;

  const inlineLyrics = String(
    data.audio?.lyrics || data.audio?.lyricsText || data.audio?.lyrics_text || ""
  ).trim();

  /**
   * IMPORTANT
   *
   * This is the URL that this component uses for playback.
   *
   * Do NOT use lyricsUrl here because lyricsUrl is being used
   * to load lyrics content.
   *
   * If your API has another field containing the special audio
   * URL for lyrics cards, replace audioUrl here with that field.
   */
  const playbackUrl = audioUrl;

  /**
   * All formats use the persistent Shaka engine in CardAudioMiniPlayer.
   * This page only syncs UI and sends play/pause/seek commands.
   */
  const miniVisible = useCardAudioShakaMiniPlayerStore((s) => s.visible);
  const miniSrc = useCardAudioShakaMiniPlayerStore((s) => s.src);
  const miniPlaying = useCardAudioShakaMiniPlayerStore((s) => s.playing);
  const miniCurrentTime = useCardAudioShakaMiniPlayerStore((s) => s.currentTime);
  const miniDuration = useCardAudioShakaMiniPlayerStore((s) => s.duration);
  const syncedWithMini = miniVisible && isSameShakaAudioSrc(miniSrc, playbackUrl || "");

  useEffect(() => {
    if (!syncedWithMini) {
      // Different track (or nothing) in the mini player — show this page at rest.
      setIsPlaying(false);
      setCurrentTime(0);
      return;
    }
    setIsPlaying(miniPlaying);
    setCurrentTime(miniCurrentTime);
    if (miniDuration > 0) setDuration(miniDuration);
  }, [syncedWithMini, miniPlaying, miniCurrentTime, miniDuration]);

  // Preview limit for unpaid playback.
  useEffect(() => {
    if (!syncedWithMini || isPurchased) return;
    if (miniDuration <= previewDurationSeconds) return;
    if (miniCurrentTime < previewDurationSeconds) return;

    const media = getShakaAudioLiveMedia();
    if (media) {
      media.pause();
      media.currentTime = previewDurationSeconds;
    }
    useCardAudioShakaMiniPlayerStore.getState().setPlayback({
      playing: false,
      currentTime: previewDurationSeconds,
    });
  }, [syncedWithMini, isPurchased, miniCurrentTime, miniDuration, previewDurationSeconds]);

  const trackKey = useMemo(() => {
    return playbackUrl || "";
  }, [playbackUrl]);

  /**
   * ---------------------------------------------------------
   * LOAD LYRICS
   * ---------------------------------------------------------
   */

  useEffect(() => {
    let cancelled = false;

    setLyrics("");

    setIsLyricsLoading(Boolean(lyricsUrl || inlineLyrics));

    const loadLyrics = async () => {
      try {
        const loadedLyrics = inlineLyrics
          ? normalizeLyricsText(inlineLyrics)
          : lyricsUrl
            ? await fetchLyricsContent(lyricsUrl)
            : "";

        if (!cancelled) {
          setLyrics(loadedLyrics);
          setIsLyricsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setLyrics("");
          setIsLyricsLoading(false);
        }
      }
    };

    void loadLyrics();

    return () => {
      cancelled = true;
    };
  }, [inlineLyrics, lyricsUrl]);

  /**
   * ---------------------------------------------------------
   * LYRICS SCROLL
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    activeLyricRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [isPlaying]);

  /**
   * ---------------------------------------------------------
   * PREVIEW
   * ---------------------------------------------------------
   */

  const previewIsLimited = !isPurchased && duration > previewDurationSeconds;

  const playableDuration = previewIsLimited ? previewDurationSeconds : duration;

  const progress =
    playableDuration > 0 ? Math.min(100, Math.max(0, (currentTime / playableDuration) * 100)) : 0;

  /**
   * ---------------------------------------------------------
   * LYRICS
   * ---------------------------------------------------------
   */

  const lyricsLines = useMemo(
    () =>
      lyrics
        .split(/\r?\n/)
        .map((line) =>
          line
            .trim()
            .replace(/^(?:\[\d{1,2}:\d{1,2}(?:\.\d{1,2})?\]\s*)+/, "")
            .trim()
        )
        .filter(Boolean),
    [lyrics]
  );

  const hasLyrics = isLyricsLoading || lyricsLines.length > 0;

  /**
   * ---------------------------------------------------------
   * PLAY / PAUSE
   * ---------------------------------------------------------
   */

  const togglePlayback = () => {
    if (!trackKey || !playbackUrl) {
      return;
    }

    // Same track already in the persistent mini engine → just play/pause.
    if (syncedWithMini) {
      const media = getShakaAudioLiveMedia();
      if (!media) return;

      if (previewIsLimited && media.currentTime >= previewDurationSeconds) {
        media.currentTime = 0;
        setCurrentTime(0);
      }

      if (media.paused) {
        void playShakaAudioExclusive(media)
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      } else {
        media.pause();
        setIsPlaying(false);
      }
      return;
    }

    // Different track (or first play) → load this content into the mini engine.
    playCardShakaAudio({
      title,
      artist: artistName,
      artwork: artworkUrl || "",
      src: playbackUrl,
      startTime: 0,
    });
    setIsPlaying(true);
  };

  /**
   * ---------------------------------------------------------
   * SEEK
   * ---------------------------------------------------------
   */

  const seekFromBar = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!trackKey || playableDuration <= 0 || !syncedWithMini) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
    const nextTime = ratio * playableDuration;

    const media = getShakaAudioLiveMedia();
    if (!media) return;
    media.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  /**
   * ---------------------------------------------------------
   * SKIP
   * ---------------------------------------------------------
   */

  const skip = (seconds: number) => {
    if (!trackKey || !syncedWithMini) {
      return;
    }

    const media = getShakaAudioLiveMedia();
    if (!media) return;
    const maxTime = playableDuration > 0 ? playableDuration : media.duration || 0;
    const nextTime = Math.min(
      Math.max(0, (media.currentTime || 0) + seconds),
      maxTime || Infinity
    );
    media.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

/**
   * ---------------------------------------------------------
   * NO AUDIO
   * ---------------------------------------------------------
   */

  if (!playbackUrl) {
    return null;
  }

  /**
   * ---------------------------------------------------------
   * COLORS
   * ---------------------------------------------------------
   */

  const colors = {
    page: darkMode ? "#0b0a10" : "#f8f8f8",

    text: darkMode ? "#f7f5fa" : "#1a1528",

    textSoft: darkMode ? "rgba(247,245,250,0.68)" : "#655f70",

    textMuted: darkMode ? "rgba(247,245,250,0.5)" : "#8a8494",

    accent: darkMode ? "#e9d5ff" : "#6d28d9",

    card: darkMode ? "rgba(255,255,255,0.08)" : "#ffffff",

    border: darkMode ? "rgba(255,255,255,0.12)" : "rgba(26,21,40,0.1)",

    play: darkMode ? "#ffffff" : "#1a1528",

    playIcon: darkMode ? "#0b0a10" : "#ffffff",
  };

  const playGrad = darkMode
    ? "linear-gradient(135deg, #6D28D9 0%, #C9A44C 100%)"
    : "linear-gradient(135deg, #7C3AED 0%, #B45309 100%)";

  const waveGrad = darkMode
    ? "linear-gradient(90deg, #C9A44C 0%, #8B5CF6 100%)"
    : "linear-gradient(90deg, #B45309 0%, #7C3AED 100%)";

  /**
   * ---------------------------------------------------------
   * PREVIEW NOTICE
   * ---------------------------------------------------------
   */

  const previewNotice = previewIsLimited ? (
    <Tooltip
      title={`Only ${formatTime(previewDurationSeconds)} can be played without payment.`}
      arrow
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
        sx={{
          color: colors.accent,
        }}
      >
        <LockOutlinedIcon
          sx={{
            fontSize: 13,
          }}
        />

        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
          }}
        >
          {previewDurationSeconds}-second preview
        </Typography>
      </Stack>
    </Tooltip>
  ) : null;

  /**
   * =========================================================
   * LYRICS VIEW
   * =========================================================
   */

  if (hasLyrics) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: colors.page,
          color: colors.text,
        }}
      >

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 2.5,
            pt: 6.25,
            pb: 1.5,
            scrollBehavior: "smooth",
            maskImage: "linear-gradient(180deg, transparent 0%, #000 4%, #000 100%)",
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: {
                xs: "1.65rem",
                sm: "1.85rem",
              },
              letterSpacing: "-0.035em",
              lineHeight: 1.12,
              mb: artistName ? 0.35 : 3.25,
            }}
          >
            {title}
          </Typography>

          {artistName && (
            <Typography
              sx={{
                color: colors.textSoft,
                fontSize: {
                  xs: "0.84rem",
                  sm: "0.9rem",
                },
                fontWeight: 500,
                lineHeight: 1.4,
                mb: 3.25,
              }}
            >
              {artistName}
            </Typography>
          )}

          {isLyricsLoading ? (
            <Typography
              sx={{
                color: colors.textMuted,
                fontSize: "1.1rem",
              }}
            >
              Loading lyrics…
            </Typography>
          ) : (
            lyricsLines.map((line, index) => {
              const isLabel = /^\[.+\]$/.test(line);

              if (isLabel) {
                return (
                  <Typography
                    key={`${index}-${line}`}
                    sx={{
                      mt: index === 0 ? 0 : 3.5,
                      mb: 1.35,
                      color: colors.accent,
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                    }}
                  >
                    {line.replace(/^\[|\]$/g, "")}
                  </Typography>
                );
              }

              return (
                <Typography
                  key={`${index}-${line}`}
                  sx={{
                    mt: 0.55,
                    py: 0.2,
                    color: "var(--neutral-900)",
                    fontSize: {
                      xs: "1.12rem",
                      sm: "1.18rem",
                    },
                    fontWeight: 500,
                    lineHeight: 1.55,
                    letterSpacing: "-0.01em",
                    transition: "all 250ms ease",
                  }}
                >
                  {line}
                </Typography>
              );
            })
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: 1.5,
            pt: 0.5,
            pb: 1.35,
            background: darkMode
              ? "linear-gradient(180deg, rgba(11,10,16,0), rgba(11,10,16,0.96) 28%)"
              : `linear-gradient(180deg, transparent, ${colors.page} 28%)`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: {
                xs: 0.65,
                sm: 1.25,
              },
              px: 1.2,
              py: 1.05,
              borderRadius: "20px",
              bgcolor: colors.card,
              border: `1px solid ${colors.border}`,
              backdropFilter: "blur(18px)",
              boxShadow: isPlaying
                ? "0 14px 36px rgba(109,40,217,0.22)"
                : "0 10px 28px rgba(0,0,0,0.14)",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                flexShrink: 0,
                bgcolor: colors.border,
                backgroundImage: artworkUrl ? `url(${artworkUrl})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  mb: 0.7,
                  color: colors.textSoft,
                  fontSize: "0.68rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {artistName || "Now playing"}
              </Typography>

              <Box
                onClick={seekFromBar}
                sx={{
                  height: 3,
                  borderRadius: 99,
                  bgcolor: colors.border,
                  cursor: "pointer",
                  overflow: "hidden",
                  mb: 0.45,
                }}
              >
                <Box
                  sx={{
                    width: `${progress}%`,
                    height: "100%",
                    background: darkMode ? waveGrad : playGrad,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.58rem",
                    color: colors.textMuted,
                  }}
                >
                  {formatTime(currentTime)}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.58rem",
                    color: colors.textMuted,
                  }}
                >
                  {formatTime(playableDuration)}
                </Typography>
              </Box>
            </Box>

            <IconButton
              onClick={() => skip(-CARD_AUDIO_SKIP_SECONDS)}
              disabled={!trackKey}
              sx={{
                color: colors.textSoft,
              }}
              aria-label="Rewind 5 seconds"
            >
              <SkipPreviousRoundedIcon
                sx={{
                  fontSize: 22,
                }}
              />
            </IconButton>

            <IconButton
              onClick={togglePlayback}
              disabled={!trackKey}
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                bgcolor: colors.play,
                color: colors.playIcon,
                "&:hover": {
                  bgcolor: colors.play,
                },
              }}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
            </IconButton>

            <IconButton
              onClick={() => skip(CARD_AUDIO_SKIP_SECONDS)}
              disabled={!trackKey}
              sx={{
                color: colors.textSoft,
              }}
              aria-label="Forward 5 seconds"
            >
              <SkipNextRoundedIcon
                sx={{
                  fontSize: 22,
                }}
              />
            </IconButton>
          </Box>

          {previewNotice && <Box sx={{ mt: 0.75 }}>{previewNotice}</Box>}
        </Box>
      </Box>
    );
  }

  /**
   * =========================================================
   * NORMAL AUDIO VIEW
   * =========================================================
   */

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: colors.page,
        color: colors.text,
      }}
    >

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          placeItems: "center",
          px: 2,
          pt: 2,
        }}
      >
        <Box
          sx={{
            height: "100%",
            maxWidth: "100%",
            aspectRatio: "1",
            position: "relative",
            overflow: "hidden",
            borderRadius: {
              xs: "14px",
              sm: "20px",
            },
            bgcolor: "#201d28",
            boxShadow: isPlaying
              ? "0 0 36px rgba(201,164,76,0.35), 0 18px 40px rgba(0,0,0,0.28)"
              : darkMode
                ? "0 18px 40px rgba(0,0,0,0.28)"
                : "0 16px 40px rgba(26,21,40,0.12)",
            transform: isPlaying ? "scale(1.01)" : "scale(1)",
            transition: "transform 400ms ease, box-shadow 400ms ease",
          }}
        >
          {artworkUrl ? (
            <Box
              component="img"
              src={artworkUrl}
              alt=""
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Typography
              sx={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                px: 2,
                color: "#ffffff",
                fontFamily: "Georgia, serif",
                fontSize: {
                  xs: "2.5rem",
                  sm: "3.4rem",
                },
                textAlign: "center",
                textShadow: "0 6px 28px rgba(0,0,0,0.5)",
              }}
            >
              {title}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          px: 2.25,
          pt: 1,
          pb: 1.25,
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.25rem", sm: "1.45rem" },
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mb: 0.35,
          }}
        >
          {title}
        </Typography>

        {artistName && (
          <Typography
            sx={{
              color: colors.textSoft,
              fontSize: {
                xs: "0.84rem",
                sm: "0.9rem",
              },
              fontWeight: 500,
              mb: 0.25,
            }}
          >
            {artistName}
          </Typography>
        )}

        {description && (
          <Typography
            sx={{
              mb: 1.15,
              color: colors.accent,
              fontFamily: "Georgia, serif",
              fontSize: { xs: "1rem", sm: "1.05rem" },
              fontWeight: 500,
              fontStyle: "italic",
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </Typography>
        )}

        {previewNotice}

        <Box
          sx={{
            mt: previewNotice ? 0.85 : 0,
            mb: 0.35,
          }}
        >
          <Box
            onClick={seekFromBar}
            sx={{
              height: 32,
              borderRadius: 1.5,
              background: playGrad,
              cursor: playableDuration > 0 ? "pointer" : "default",
              WebkitMaskImage:
                "repeating-linear-gradient(90deg, #000 0 2.5px, transparent 2.5px 5px)",
              maskImage: "repeating-linear-gradient(90deg, #000 0 2.5px, transparent 2.5px 5px)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(90deg, transparent ${progress}%, rgba(0,0,0,0.45) ${progress}%)`,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 0.35,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                color: colors.textMuted,
              }}
            >
              {formatTime(currentTime)}
            </Typography>

            <Typography
              sx={{
                fontSize: "0.62rem",
                color: colors.textMuted,
              }}
            >
              {formatTime(playableDuration)}
            </Typography>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={2.5}
          justifyContent="center"
          alignItems="center"
          sx={{
            pt: 0.35,
          }}
        >
          <IconButton
            onClick={() => skip(-CARD_AUDIO_SKIP_SECONDS)}
            disabled={!trackKey}
            sx={{
              color: colors.text,
            }}
            aria-label="Rewind 5 seconds"
          >
            <SkipPreviousRoundedIcon
              sx={{
                fontSize: 28,
              }}
            />
          </IconButton>

          <IconButton
            onClick={togglePlayback}
            disabled={!trackKey}
            aria-label={isPlaying ? "Pause" : "Play"}
            sx={{
              width: 60,
              height: 60,
              background: playGrad,
              color: "#ffffff",
              boxShadow: "0 8px 22px rgba(201,164,76,0.28)",
              "&:hover": {
                background: playGrad,
              },
            }}
          >
            {isPlaying ? (
              <PauseRoundedIcon
                sx={{
                  fontSize: 30,
                }}
              />
            ) : (
              <PlayArrowRoundedIcon
                sx={{
                  fontSize: 34,
                }}
              />
            )}
          </IconButton>

          <IconButton
            onClick={() => skip(CARD_AUDIO_SKIP_SECONDS)}
            disabled={!trackKey}
            sx={{
              color: colors.text,
            }}
            aria-label="Forward 5 seconds"
          >
            <SkipNextRoundedIcon
              sx={{
                fontSize: 28,
              }}
            />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
};
